import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type AdminStats = {
    totalUsers: number;
    totalProducts: number;
    totalCategories: number;
    totalOrders: number;
    pendingQuotes: number;
    pendingSpecialRequests: number;
    pendingContactRequests: number;
    totalRevenue: number;
};

export const useAdminStats = () => {
    const [stats, setStats] = useState<AdminStats>({
        totalUsers: 0,
        totalProducts: 0,
        totalCategories: 0,
        totalOrders: 0,
        pendingQuotes: 0,
        pendingSpecialRequests: 0,
        pendingContactRequests: 0,
        totalRevenue: 0
    });
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const [
                usersResult,
                productsResult,
                categoriesResult,
                ordersResult,
                quotesResult,
                specialRequestsResult,
                contactRequestsResult
            ] = await Promise.all([
                supabase.from('profiles').select('id', { count: 'exact' }),
                supabase.from('products').select('id, price_per_unit, total_inventory', { count: 'exact' }),
                supabase.from('categories').select('id', { count: 'exact' }),
                supabase.from('orders').select('id, total_amount', { count: 'exact' }),
                // Use secure RPC functions for customer data access
                supabase.rpc('get_secure_quote_requests').then(result => ({
                    data: result.data?.filter((q: any) => q.status === 'pending') || [],
                    error: result.error
                })),
                supabase.rpc('get_secure_special_search_requests').then(result => ({
                    data: result.data?.filter((r: any) => r.status === 'pending') || [],
                    error: result.error
                })),
                supabase.from('contact_requests').select('id', { count: 'exact' }).eq('status', 'pending')
            ]);

            const totalRevenue = ordersResult.data?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;

            setStats({
                totalUsers: usersResult.count || 0,
                totalProducts: productsResult.count || 0,
                totalCategories: categoriesResult.count || 0,
                totalOrders: ordersResult.count || 0,
                pendingQuotes: quotesResult.data?.length || 0,
                pendingSpecialRequests: specialRequestsResult.data?.length || 0,
                pendingContactRequests: contactRequestsResult.count || 0,
                totalRevenue
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    return { stats, loading, refetch: fetchStats };
};
